import { Button, Card, Input, message, Popconfirm, Select, Spin, Table, Modal } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
  UndoOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../utils/helper';
import './template-list-oodocs.css';
import EditTemplateOodocs from './EditTemplateOodocs';
import TemplateContentModal from './TemplateContentModal';
import AddTemplateOodocs from '../AddTemplateOodocs';
import DateFormatter from '../../components/DateFormatter';
import ErrorBoundary from '../../components/ErrorBoundary';
import { BASE_API } from '../../axios';
import { TokenStorage } from '../../utils/cookieStorage';

const { Search } = Input;
const { Option } = Select;

const formatFileSize = (bytes: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Sentinel for the centre filter — a real centre code can never collide with it. */
const GLOBAL_CENTRE = '__global__';

/**
 * A template's centres, tolerant of both storage shapes.
 *
 * dt_center_codes (array) is what centre scoping actually reads. dt_center_code is
 * the legacy single-value column, still populated. Falling back to it matters for
 * rows written before the array existed — reading only the array would show them as
 * global when the backend might not treat them that way.
 */
const centresOf = (record: any): string[] => {
  if (Array.isArray(record?.dt_center_codes) && record.dt_center_codes.length) {
    return record.dt_center_codes;
  }
  return record?.dt_center_code ? [record.dt_center_code] : [];
};

interface TemplateListOodocsProps {
  /**
   * Self-service mode: the "Templates" screen radiologists / technicians open
   * from the profile menu. Same table, but scoped to what they're allowed to
   * see (global templates + their own) and to what they may edit.
   */
  selfService?: boolean;
}

const TemplateListOodocs = ({ selfService = false }: TemplateListOodocsProps = {}) => {
  const [modalitiesList, setModalitiesList] = useState([]);
  const [bodyPartsList, setBodyPartsList] = useState([]);
  const [templatesList, setTemplatesList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [selectedActive, setSelectedActive] = useState('');
  const [selectedOwnership, setSelectedOwnership] = useState('all'); // all | shared | private
  const [selectedOwner, setSelectedOwner] = useState(null);          // specific owner username
  // '' = no filter, GLOBAL_CENTRE = only templates with no centre, else a centre code.
  const [selectedCentre, setSelectedCentre] = useState('');
  const [centresList, setCentresList] = useState([]);

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [contentModalVisible, setContentModalVisible] = useState(false);
  const [contentModalMode, setContentModalMode] = useState<'view' | 'edit'>('view');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState(false);

  const currentUser = getUserDetails();
  const isHOD = currentUser?.user_type === 'hod';
  // Admin/HOD have full template privileges — same rule the backend applies.
  const hasFullTemplateAccess = ['admin', 'hod'].includes(currentUser?.user_type);
  // Anyone on the self-service screen may add a template (globally or private);
  // in the admin panel it stays an HOD action.
  const canAddTemplate = isHOD || selfService;

  // Mirrors the backend's canModifyTemplate(): admin/HOD edit anything, everyone
  // else only their own private templates or global ones they added themselves.
  const canModifyTemplate = useCallback((record: any) => {
    if (hasFullTemplateAccess) return true;
    if (record.dt_owner_user_id != null) {
      return Number(record.dt_owner_user_id) === Number(currentUser?.user_id);
    }
    return !!currentUser?.username && record.dt_added_by === currentUser.username;
  }, [hasFullTemplateAccess, currentUser?.user_id, currentUser?.username]);

  const getTemplatesList = useCallback(() => {
    setTemplatesList({ loading: true, data: [], error: null });
    makePostCall('/template-list-oodocs', { active_only: false, ...(selfService ? { owner_scope: 'self' } : {}) })
      .then(res => {
        const templatesData = res.data?.data || [];
        setTemplatesList({ data: templatesData, loading: false, error: null });
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load templates';
        setTemplatesList({ data: [], loading: false, error: errorMessage });
        message.error(`Error loading templates: ${errorMessage}`);
      });
  }, [selfService]);

  const getModalitiesList = useCallback(() => {
    makeGetCall('/modalities')
      .then(res => setModalitiesList(res.data?.data || []))
      .catch(e => message.error(`Error loading modalities: ${e.response?.data?.message || e.message}`));
  }, []);

  const getBodyPartsList = useCallback(() => {
    makeGetCall('/body-parts')
      .then(res => setBodyPartsList(res.data?.data || []))
      .catch(e => message.error(`Error loading body parts: ${e.response?.data?.message || e.message}`));
  }, []);

  // Centres, so the column can show "Somajiguda" rather than the raw "SMJ1" code.
  // Failure here is not worth a toast — the column falls back to showing codes.
  const getCentresList = useCallback(() => {
    makeGetCall('/centres?active=true')
      .then(res => setCentresList(res.data?.data || []))
      .catch(e => console.error('Failed to fetch centres:', e));
  }, []);

  useEffect(() => {
    getModalitiesList();
    getBodyPartsList();
    getTemplatesList();
    if (!selfService) getCentresList();
  }, [getModalitiesList, getBodyPartsList, getTemplatesList, getCentresList, selfService]);

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    let filtered = templatesList.data;

    if (searchText) {
      filtered = filtered.filter(
        (t: any) =>
          t.dt_template_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          t.dt_display_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (selectedModality) {
      filtered = filtered.filter((t: any) => t.dt_modality === selectedModality);
    }
    if (selectedBodyPart) {
      filtered = filtered.filter((t: any) => t.dt_bodypart === selectedBodyPart);
    }
    if (selectedActive) {
      filtered = filtered.filter((t: any) =>
        selectedActive === 'N' ? t.dt_active === 'Y' && t.dt_deleted === 'N' : t.dt_active !== 'Y' || t.dt_deleted === 'Y'
      );
    }
    if (selectedOwnership === 'shared') {
      filtered = filtered.filter((t: any) => !t.dt_owner_user_id);
    } else if (selectedOwnership === 'private') {
      filtered = filtered.filter((t: any) => !!t.dt_owner_user_id);
    }
    if (selectedOwner) {
      filtered = filtered.filter((t: any) => t.dt_owner_username === selectedOwner);
    }
    if (selectedCentre) {
      filtered = selectedCentre === GLOBAL_CENTRE
        // "Global" means no centre at all — every personal template, plus shared
        // ones nobody scoped. These are the templates visible at every centre.
        ? filtered.filter((t: any) => centresOf(t).length === 0)
        : filtered.filter((t: any) => centresOf(t).includes(selectedCentre));
    }
    return filtered;
  }, [templatesList.data, searchText, selectedModality, selectedBodyPart, selectedActive, selectedOwnership, selectedOwner, selectedCentre]);

  // code -> display name, so the column reads "Somajiguda" instead of "SMJ1".
  const centreNameByCode = useMemo(() => {
    return Object.fromEntries((centresList || []).map((c: any) => [c.code, c.name]));
  }, [centresList]);

  // Centre codes actually present on templates, unioned with the master list. The
  // union matters both ways: a centre with no templates yet should still be
  // selectable, and a code left on a template after its centre was deactivated
  // should still be findable rather than silently unfilterable.
  const centreOptions = useMemo(() => {
    const used = new Set<string>();
    (templatesList.data || []).forEach((t: any) => centresOf(t).forEach(c => used.add(c)));
    (centresList || []).forEach((c: any) => c?.code && used.add(c.code));
    return Array.from(used).sort();
  }, [templatesList.data, centresList]);

  // Distinct owners present in the data (for the Owner filter dropdown)
  const ownerOptions = useMemo(() => {
    const names = templatesList.data
      .map((t: any) => t.dt_owner_username)
      .filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [templatesList.data]);

  // Body parts filtered by selected modality
  const filteredBodyPartsList = useMemo(() => {
    if (!selectedModality) {
      return bodyPartsList.filter(
        (item: any, index, self) =>
          index === self.findIndex((bp: any) => (bp.code || bp.value) === (item.code || item.value))
      );
    }
    const availableBodyParts = templatesList.data
      .filter((t: any) => t.dt_modality === selectedModality)
      .map((t: any) => t.dt_bodypart)
      .filter((v, i, self) => self.indexOf(v) === i);

    return bodyPartsList.filter((bp: any) =>
      bp.modality === selectedModality && availableBodyParts.includes(bp.code || bp.value)
    );
  }, [selectedModality, bodyPartsList, templatesList.data]);

  useEffect(() => {
    if (selectedModality) setSelectedBodyPart(null);
  }, [selectedModality]);

  const editTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setEditModalVisible(true);
  }, []);

  const openContentModal = useCallback((template, mode: 'view' | 'edit') => {
    setSelectedTemplate(template);
    setContentModalMode(mode);
    setContentModalVisible(true);
  }, []);

  const previewTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setPreviewModalVisible(true);
  }, []);

  const deleteTemplate = useCallback((template, action: string) => {
    const msg = action === 'D' ? 'Template deleted successfully'
      : action === 'A' ? 'Template activated successfully'
      : 'Template deleted permanently';

    setDeleteInProgress(true);
    makePostCall('/delete-template-oodocs', { dt_id: template.dt_id, action })
      .then(res => {
        if (res.data?.success) {
          message.success(msg);
          getTemplatesList();
        } else {
          message.error(res.data?.message || 'Failed to update template');
        }
      })
      .catch(e => message.error(e.response?.data?.message || e.message || 'Failed to update template'))
      .finally(() => setDeleteInProgress(false));
  }, [getTemplatesList]);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedModality('');
    setSelectedBodyPart('');
    setSelectedActive('');
    setSelectedOwnership('all');
    setSelectedOwner(null);
    setSelectedCentre('');
  }, []);

  const getModalityLabel = useCallback(
    (code) => {
      if (!code) return 'N/A';
      const m = modalitiesList.find((m: any) => m.code === code || m.value === code);
      return m ? (m as any).label : code;
    },
    [modalitiesList]
  );

  const getBodyPartLabel = useCallback(
    (code) => {
      if (!code) return 'N/A';
      const bp = bodyPartsList.find((bp: any) => bp.code === code || bp.value === code);
      return bp ? (bp as any).label : code;
    },
    [bodyPartsList]
  );

  const handleDownloadTemplate = useCallback(async (template: any) => {
    try {
      const token = TokenStorage.getAccessToken();
      const res = await fetch(`${BASE_API}/template-url-oodocs/${template.dt_id}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json();
      if (json.success && json.data?.url) {
        const a = document.createElement('a');
        a.href = json.data.url;
        a.download = `${template.dt_template_name || 'template'}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        message.error('Failed to get download URL');
      }
    } catch {
      message.error('Failed to download template');
    }
  }, []);

  const TEMPLATE_COLUMNS = [
    {
      title: 'Template Name',
      key: 'template_name',
      render: (_, record) => record.dt_template_name || 'N/A',
    },
    {
      title: 'Display Name',
      key: 'display_name',
      render: (_, record) => record.dt_display_name || 'N/A',
    },
    {
      title: 'Modality',
      key: 'modality',
      render: (_, record) => getModalityLabel(record.dt_modality),
    },
    {
      title: 'Body Part',
      key: 'bodypart',
      render: (_, record) => getBodyPartLabel(record.dt_bodypart),
    },
    {
      title: selfService ? 'Availability' : 'Owner',
      key: 'owner',
      render: (_, record) =>
        record.dt_owner_user_id
          ? (
            <span className="owner-private">
              {selfService ? 'Only me' : (record.dt_owner_username || `user #${record.dt_owner_user_id}`)}
            </span>
          )
          : <span className="owner-shared">{selfService ? 'Everyone' : 'Shared'}</span>,
    },
    // Admin-only, like the Owner column. A self-service user's own templates are
    // always global, so the column would be a constant "All centres" for them.
    ...(selfService ? [] : [{
      title: 'Centres',
      key: 'centres',
      render: (_, record) => {
        const codes = centresOf(record);
        if (!codes.length) {
          return <span className="centre-global" title="Visible at every centre">All centres</span>;
        }
        return (
          <span className="centre-badges">
            {codes.map((code: string) => (
              <span key={code} className="centre-badge" title={code}>
                {centreNameByCode[code] || code}
              </span>
            ))}
          </span>
        );
      },
    }]),
    {
      title: 'File Size',
      key: 'file_size',
      render: (_, record) => (
        <span className="file-size-badge">{formatFileSize(record.dt_file_size)}</span>
      ),
    },
    {
      title: 'Added By',
      key: 'added_by',
      render: (_, record) => record.dt_added_by || 'N/A',
    },
    {
      title: 'Added Date',
      key: 'added_dt',
      render: (_, record) => <DateFormatter date={record.dt_created_at} />,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <span className={record.dt_deleted === 'N' && record.dt_active === 'Y' ? 'status-active' : 'status-inactive'}>
          {record.dt_deleted === 'N' && record.dt_active === 'Y' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        // Read-only rows (global templates someone else owns) keep View but lose
        // every write action — the backend rejects them anyway.
        const canModify = canModifyTemplate(record);
        const notOwnerTitle = 'You can only edit your own templates';
        return (
          <div className="action-buttons">
            <Button
              onClick={() => openContentModal(record, 'view')}
              style={{ marginRight: 3 }}
              title="View Template Content"
            >
              <EyeOutlined />
            </Button>
            <Button
              onClick={() => editTemplate(record)}
              style={{ marginRight: 3 }}
              title={canModify ? 'Edit Template Metadata' : notOwnerTitle}
              disabled={record.dt_deleted === 'Y' || !canModify}
            >
              <EditOutlined />
            </Button>
            <Button
              onClick={() => openContentModal(record, 'edit')}
              style={{ marginRight: 3 }}
              title={canModify ? 'Edit Template Content' : notOwnerTitle}
              disabled={record.dt_deleted === 'Y' || !canModify}
            >
              <FileTextOutlined />
            </Button>
            {!canModify ? null : record.dt_deleted === 'N' ? (
              <Popconfirm
                title="Delete template?"
                description="This action will deactivate the template."
                onConfirm={() => deleteTemplate(record, 'D')}
                okText="Yes"
                cancelText="No"
              >
                <Button title="Delete template"><DeleteOutlined /></Button>
              </Popconfirm>
            ) : (
              <>
                <Popconfirm
                  title="Restore template?"
                  description="This will make the template active again."
                  onConfirm={() => deleteTemplate(record, 'A')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button title="Restore template"><UndoOutlined /></Button>
                </Popconfirm>
                <Popconfirm
                  title="Delete permanently?"
                  description="This action cannot be undone. The file will be removed."
                  onConfirm={() => deleteTemplate(record, 'X')}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger title="Delete permanently"><DeleteOutlined /></Button>
                </Popconfirm>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <ErrorBoundary>
      <Spin
        spinning={templatesList?.loading || deleteInProgress}
        tip={deleteInProgress ? 'Deleting template...' : 'Loading templates...'}
      >
        <div className="templates-oodocs-container admin-container">
          {templatesList.error && (
            <div className="error-container">
              <strong>Error:</strong> {templatesList.error}
              <Button type="link" size="small" onClick={getTemplatesList} className="retry-button">Retry</Button>
            </div>
          )}
          <div>
            <Card bordered={false} style={{ width: '100%', margin: '1rem auto' }} className="admin-card">
              <div className="template-oodocs-header admin-header">
                <div className="page-title admin-page-title">
                  {selfService ? 'TEMPLATES' : 'TEMPLATES (DOCX)'}
                </div>
                {canAddTemplate && (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => setAddTemplateModalVisible(true)}
                    style={{ marginRight: 16 }}
                  >
                    Add Template
                  </Button>
                )}
                <div className="template-oodocs-filters-inline admin-filters-inline">
                  <Search
                    placeholder="Search by template name or display name"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300, marginRight: 16 }}
                    prefix={<SearchOutlined />}
                  />
                  <Select
                    placeholder="Filter by Modality"
                    value={selectedModality || undefined}
                    onChange={setSelectedModality}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    {modalitiesList.map((m: any) => (
                      <Option key={m.code || m.value} value={m.code || m.value}>{m.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder={ !selectedModality ? 'Select modality first' : filteredBodyPartsList.length > 0
                          ? `Filter by Body Part (${filteredBodyPartsList.length})`
                          : 'No body parts available'
                    }
                    value={selectedBodyPart || undefined}
                    onChange={setSelectedBodyPart}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                    disabled={!selectedModality || filteredBodyPartsList.length === 0}
                    notFoundContent={selectedModality ? 'No body parts for selected modality' : 'Select a modality first'}
                  >
                    {filteredBodyPartsList.map((bp: any) => (
                      <Option key={bp.code || bp.value} value={bp.code || bp.value}>{bp.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Filter by Status"
                    value={selectedActive}
                    onChange={setSelectedActive}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    <Option key="All" value="">All</Option>
                    <Option key="Active" value="N">Active</Option>
                    <Option key="InActive" value="Y">Inactive</Option>
                  </Select>
                  <Select
                    placeholder={selfService ? 'Filter by Availability' : 'Filter by Ownership'}
                    value={selectedOwnership}
                    onChange={(v) => { setSelectedOwnership(v); if (v !== 'private') setSelectedOwner(null); }}
                    style={{ width: 180, marginRight: 16 }}
                  >
                    <Option key="all" value="all">{selfService ? 'All templates' : 'All ownership'}</Option>
                    <Option key="shared" value="shared">{selfService ? 'Everyone' : 'Shared'}</Option>
                    <Option key="private" value="private">{selfService ? 'Only me' : 'Private'}</Option>
                  </Select>
                  {/* Centre filter is admin-only, like the Owner filter below.
                      "All centres" selects the templates with NO centre — the ones
                      every centre can see — which is the question an admin actually
                      asks when a template turns up somewhere unexpected. */}
                  {!selfService && (
                    <Select
                      placeholder="Filter by Centre"
                      value={selectedCentre || undefined}
                      onChange={(v) => setSelectedCentre(v || '')}
                      style={{ width: 200, marginRight: 16 }}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      <Option key={GLOBAL_CENTRE} value={GLOBAL_CENTRE}>All centres (global)</Option>
                      {centreOptions.map((code: string) => (
                        <Option key={code} value={code}>
                          {centreNameByCode[code] ? `${centreNameByCode[code]} (${code})` : code}
                        </Option>
                      ))}
                    </Select>
                  )}
                  {/* Owner filter is admin-only — in self-service every private
                      template in the list already belongs to the caller. */}
                  {!selfService && (
                    <Select
                      placeholder={`Filter by Owner${ownerOptions.length ? ` (${ownerOptions.length})` : ''}`}
                      value={selectedOwner || undefined}
                      onChange={setSelectedOwner}
                      style={{ width: 200, marginRight: 16 }}
                      allowClear
                      showSearch
                      disabled={ownerOptions.length === 0}
                      notFoundContent="No private-template owners"
                    >
                      {ownerOptions.map((name: string) => (
                        <Option key={name} value={name}>{name}</Option>
                      ))}
                    </Select>
                  )}
                  <Button onClick={clearFilters}>Clear Filters</Button>
                </div>
              </div>

              <Table
                className="template-oodocs-list-table admin-table"
                columns={TEMPLATE_COLUMNS}
                dataSource={filteredTemplates}
                rowKey={(record: any) => record.dt_id}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
                }}
              />
            </Card>
          </div>
        </div>

        {/* Add Template Modal */}
        <Modal
          title="Add Template (.docx)"
          open={addTemplateModalVisible}
          onCancel={() => setAddTemplateModalVisible(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          <AddTemplateOodocs
            selfService={selfService}
            onSuccess={() => {
              setAddTemplateModalVisible(false);
              getTemplatesList();
            }}
          />
        </Modal>

        {/* Template Content View/Edit Modal */}
        <TemplateContentModal
          visible={contentModalVisible}
          templateData={selectedTemplate}
          mode={contentModalMode}
          onClose={() => setContentModalVisible(false)}
          onSuccess={() => {
            setContentModalVisible(false);
            getTemplatesList();
          }}
        />

        {/* Edit Template Modal */}
        <EditTemplateOodocs
          visible={editModalVisible}
          templateData={selectedTemplate}
          modalitiesList={modalitiesList}
          bodyPartsList={bodyPartsList}
          centresList={centresList}
          onCancel={() => setEditModalVisible(false)}
          onSuccess={() => {
            setEditModalVisible(false);
            getTemplatesList();
          }}
        />

        {/* Preview Template Modal */}
        <Modal
          title="Template Preview"
          open={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => selectedTemplate && handleDownloadTemplate(selectedTemplate)}>
              Download .docx
            </Button>,
            <Button key="close" onClick={() => setPreviewModalVisible(false)}>Close</Button>,
          ]}
          width={600}
        >
          {selectedTemplate && (
            <div className="template-oodocs-preview">
              <div className="preview-meta">
                <p><strong>Template Name:</strong> {(selectedTemplate as any).dt_template_name}</p>
                <p><strong>Display Name:</strong> {(selectedTemplate as any).dt_display_name}</p>
                <p><strong>Modality:</strong> {getModalityLabel((selectedTemplate as any).dt_modality)}</p>
                <p><strong>Body Part:</strong> {getBodyPartLabel((selectedTemplate as any).dt_bodypart)}</p>
                <p><strong>File Size:</strong> {formatFileSize((selectedTemplate as any).dt_file_size)}</p>
                <p><strong>Added By:</strong> {(selectedTemplate as any).dt_added_by || 'N/A'}</p>
                {(selectedTemplate as any).dt_comments && (
                  <p><strong>Comments:</strong> {(selectedTemplate as any).dt_comments}</p>
                )}
                <p><strong>Status:</strong>{' '}
                  <span className={(selectedTemplate as any).dt_deleted === 'N' && (selectedTemplate as any).dt_active === 'Y' ? 'status-active' : 'status-inactive'}>
                    {(selectedTemplate as any).dt_deleted === 'N' && (selectedTemplate as any).dt_active === 'Y' ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          )}
        </Modal>
      </Spin>
    </ErrorBoundary>
  );
};

export default TemplateListOodocs;
